import { Group, Stack, Tabs, Text } from "@mantine/core"
import { Badge, Loader } from "@tfg/ui"
import { isEmpty, partition } from "lodash"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { useGenerateAlerts } from "~/gen/api"
import { DashboardAlerts } from "~/pages/Brand/Home/components/DashboardAlerts"
import DashboardTabContainer from "~/pages/Brand/Home/components/DashboardTabContainer"
import { EmailNotification } from "~/pages/Brand/Home/components/EmailNotification.tsx"
import { useListNotificationsQuery } from "~/pages/Brand/Home/hooks/useListNotificationsQuery.ts"
import { useAuth } from "~/store/auth-context"

type Tab = "pending" | "completed" | "alerts"

export const Notifications = () => {
  const { t } = useTranslation(["home"])
  const [activeTab, setActiveTab] = useState<Tab>("alerts")

  const { data: notificationsData, isPending: isLoadingNotifications } =
    useListNotificationsQuery()

  const { user } = useAuth()

  const { data: alertsData, isPending: isLoadingAlerts } = useGenerateAlerts(
    {
      organizationId: user?.organization?.id ?? "",
    },
    {
      query: {
        enabled: Boolean(user?.organization?.id),
      },
    }
  )

  if (isLoadingNotifications) return <Loader fullPage />

  const [completedNotifications, pendingNotifications] = partition(
    notificationsData,
    (n) => n.archivedAt !== null
  )

  return (
    <Stack h="100%">
      <Tabs
        value={activeTab}
        onChange={(value) => setActiveTab(value as Tab)}
        h="100%"
      >
        <Tabs.List>
          <Tabs.Tab value="alerts">
            <Group>
              <Text fz="sm" fw={activeTab === "alerts" ? 500 : 400}>
                {t("notifications.tab.alerts")}
              </Text>
              <Badge
                size="xs"
                variant="primary-critical"
                text={alertsData?.data.total?.toString() ?? "0"}
              />
            </Group>
          </Tabs.Tab>
          <Tabs.Tab value="pending">
            <Group>
              <Text fz="sm" fw={activeTab === "pending" ? 500 : 400}>
                {t("notifications.tab.pending")}
              </Text>
              <Badge
                size="xs"
                bg="var(--mantine-color-orange-7)"
                c="var(--mantine-color-white)"
                text={pendingNotifications.length.toString()}
              />
            </Group>
          </Tabs.Tab>
          <Tabs.Tab value="completed">
            <Group>
              <Text fz="sm" fw={activeTab === "completed" ? 500 : 400}>
                {t("notifications.tab.completed")}
              </Text>
              <Badge
                size="xs"
                variant="info-blue"
                text={completedNotifications.length.toString()}
              />
            </Group>
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="alerts">
          <DashboardTabContainer>
            <DashboardAlerts
              isLoading={isLoadingAlerts}
              dashboardAlerts={alertsData?.data}
              hasAlerts={Number(alertsData?.data.total) > 0}
            />
          </DashboardTabContainer>
        </Tabs.Panel>
        {["pending", "completed"].map((tab) => {
          const filteredNotifications =
            tab === "pending" ? pendingNotifications : completedNotifications
          return (
            <Tabs.Panel key={tab} value={tab} mih="100%">
              <DashboardTabContainer>
                {isEmpty(filteredNotifications) ? (
                  <Group justify="center" gap="sm">
                    <Text fz="sm" style={{ textAlign: "center" }}>
                      {tab === "pending"
                        ? t("notifications.empty.pending")
                        : t("notifications.empty.completed")}
                    </Text>
                  </Group>
                ) : (
                  filteredNotifications.map((notification) => {
                    return (
                      <EmailNotification
                        key={notification.id}
                        notification={notification}
                      />
                    )
                  })
                )}
              </DashboardTabContainer>
            </Tabs.Panel>
          )
        })}
      </Tabs>
    </Stack>
  )
}
